import type { Entity } from '../components'
import type { GameInput } from '../controls'
import { applyPlayerRadius } from '../growth'
import { powerLevel } from '../powers'
import { playSfx } from '../../engine/audio'
import type { EventBus } from '../events'
import { T } from '../tunables'

// Post-unification actives: dash is INNATE (the core mastery verb — every
// fresh cell has it); shield and engulf are card adaptations
// (membrane-lock / hypertrophy) whose numbers scale with level.

export function skillActiveSystem(player: Entity, input: GameInput, dt: number, events: EventBus): void {
  // ── Dash (innate) ───────────────────────────────────────────────────────────
  if (player.dash) {
    player.dash.cooldownRemaining = Math.max(0, player.dash.cooldownRemaining - dt)
    player.dash.activeRemaining = Math.max(0, player.dash.activeRemaining - dt)

    if (input.actions.justPressedWithin('dash') && player.dash.cooldownRemaining === 0) {
      input.actions.consume('dash')

      let nx: number, ny: number
      // Dash is a movement verb, so it follows the steering heading; aim
      // (cursor when the mouse is live, heading otherwise) is only a
      // fallback — never assisted, dash always goes exactly where aimed.
      const steer = input.actions.vec('move')
      if (steer.x !== 0 || steer.y !== 0) {
        // Normalize: dash is full-strength regardless of stick deflection.
        const slen = Math.hypot(steer.x, steer.y)
        nx = steer.x / slen
        ny = steer.y / slen
      } else if (player.aim) {
        nx = player.aim.dir.x
        ny = player.aim.dir.y
      } else {
        // No aim (aimSystem didn't run) — fall back to current velocity direction.
        const vx = player.velocity?.x ?? 0
        const vy = player.velocity?.y ?? 0
        const vlen = Math.sqrt(vx * vx + vy * vy)
        nx = vlen > 0.001 ? vx / vlen : 1
        ny = vlen > 0.001 ? vy / vlen : 0
      }

      player.dash.impulse.x = nx * T.skills.dash.speed
      player.dash.impulse.y = ny * T.skills.dash.speed
      player.dash.activeRemaining = T.skills.dash.duration
      // "of the Vacuole" (item affix) shortens the dash cooldown via mods.
      player.dash.cooldownRemaining = T.skills.dash.cooldown * (player.mods?.dashCooldownMult ?? 1)
      player.invincible = Math.max(player.invincible ?? 0, T.skills.dash.iframes)
      playSfx('dash_whoosh', { volume: 0.45 })
    }
  }

  // ── Shield — Membrane Lock adaptation ──────────────────────────────────────
  if (player.shield) {
    player.shield.cooldownRemaining = Math.max(0, player.shield.cooldownRemaining - dt)
    player.shield.activeRemaining = Math.max(0, player.shield.activeRemaining - dt)

    const lockLevel = powerLevel(player, 'membrane-lock')
    if (
      input.actions.justPressed('shield') &&
      lockLevel > 0 &&
      player.shield.cooldownRemaining === 0
    ) {
      const c = T.powers.membraneLock
      player.shield.activeRemaining = c.durationBase + c.durationPerLevel * (lockLevel - 1)
      player.shield.cooldownRemaining = Math.max(2, c.cooldownBase - c.cooldownShave * (lockLevel - 1))
      playSfx('shield_up', { volume: 0.5 })
    }
  }

  // ── Engulf — Hypertrophy adaptation ─────────────────────────────────────────
  if (player.engulf) {
    player.engulf.cooldownRemaining = Math.max(0, player.engulf.cooldownRemaining - dt)
    const wasActive = player.engulf.activeRemaining > 0
    player.engulf.activeRemaining = Math.max(0, player.engulf.activeRemaining - dt)

    const hyperLevel = powerLevel(player, 'hypertrophy')
    if (
      input.actions.justPressed('engulf') &&
      hyperLevel > 0 &&
      player.engulf.cooldownRemaining === 0
    ) {
      player.engulf.activeRemaining = T.skills.engulf.duration
      player.engulf.cooldownRemaining = Math.max(
        4,
        T.skills.engulf.cooldown - T.powers.hypertrophy.engulfCooldownShave * (hyperLevel - 1),
      )
      playSfx('engulf_swell', { volume: 0.55 })
    }

    // Apply / remove the temporary radius bonus on state transitions.
    // Engulf hijacks sizeFromEating for the bonus — comment in growth.ts explains the coupling.
    const isActive = player.engulf.activeRemaining > 0
    if (!wasActive && isActive) {
      events.emit({ type: 'skill.activated', skill: 'size' })
      player.sizeFromEating = (player.sizeFromEating ?? 0) + T.skills.engulf.radiusBonus
      applyPlayerRadius(player)
    } else if (wasActive && !isActive) {
      player.sizeFromEating = Math.max(0, (player.sizeFromEating ?? 0) - T.skills.engulf.radiusBonus)
      applyPlayerRadius(player)
    }
  }
}
